import React from "react";
import type { PDPStockLevelIndicator } from "./PDPStockLevelIndicator";
import { InterpolateLinearlyReact } from "../../../../domain/translations/interpolate-linearly-react";
import { css } from "@emotion/react";
import { CSS_COLORS } from "../../../../styles/styles-css-variables";
import { FONTS, fontSize } from "../../../../styles/fonts";
import { usePdpTranslations } from "../../PdpTranslationsContext";
import cn from "classnames";
import { T, TWrapper } from "../../../../components/T";
export const StockLevelIndicator: React.FC<{
  stockLevel: PDPStockLevelIndicator;
}> = ({ stockLevel }) => {
  const t = usePdpTranslations();

  switch (stockLevel.tag) {
    case "in-stock": {
      return (
        <TextWithDot color="green">
          <T>{t("stockLevelIndicator.inStock")}</T>
        </TextWithDot>
      );
    }
    case "low-stock": {
      return (
        <TextWithDot color="yellow">
          <TWrapper
            forcedValue={
              stockLevel.count === 1
                ? t("stockLevelIndicator.lowStock.singular")
                : t("stockLevelIndicator.lowStock.plural")
            }
          >
            <InterpolateLinearlyReact
              translationStringWithInterpolatedValues={
                stockLevel.count === 1
                  ? t("stockLevelIndicator.lowStock.singular").value
                  : t("stockLevelIndicator.lowStock.plural").value
              }
              args={[stockLevel.count]}
            />
          </TWrapper>
        </TextWithDot>
      );
    }
    case "out-of-stock": {
      return (
        <TextWithDot color="red">
          <T>{t("stockLevelIndicator.outOfStock")}</T>
        </TextWithDot>
      );
    }
    case "at-dealers-only": {
      return (
        <TextWithDot color="grey">
          <T>{t("availability.atDealersOnly")}</T>
        </TextWithDot>
      );
    }
    case "discontinued": {
      return (
        <TextWithDot color="grey">
          <T>{t("availability.discontinued")}</T>
        </TextWithDot>
      );
    }
    case "not-yet-available-for-sale": {
      return (
        <TextWithDot color="yellow">
          <T>{t("availability.notYetAvailableForSale")}</T>
        </TextWithDot>
      );
    }
    case "preorder": {
      return (
        <TextWithDot color="green">
          <T>{t("availability.preorder")}</T>
        </TextWithDot>
      );
    }
    default: {
      const _: never = stockLevel;
      return null;
    }
  }
};

const TextWithDot: React.FC<{
  children: React.ReactNode;
  color: "green" | "yellow" | "red" | "grey";
}> = ({ children, color }) => {
  return (
    <span
      className={cn("so-color-black u-relative u-flex", color)}
      css={[
        FONTS.SwarovskiFutura300,
        fontSize.x2,
        css`
          margin-left: 12px;

          &.green {
            --color: ${CSS_COLORS.Green};
          }

          &.yellow {
            --color: ${CSS_COLORS.OrangeCustom};
          }

          &.red {
            --color: ${CSS_COLORS.RedCustom};
          }

          &.grey {
            --color: ${CSS_COLORS.Gray2024};
          }

          &:before {
            content: "";
            position: absolute;
            top: calc(50% - 4px);
            left: -10px;
            width: 6px;
            height: 6px;

            background-color: var(--color);
            border-radius: 2px;
          }
        `,
      ]}
    >
      {children}
    </span>
  );
};
